import {Injectable, PipeTransform} from '@angular/core';
import {BehaviorSubject, catchError, debounceTime, delay, map, Observable, of, Subject, switchMap, tap} from "rxjs";
import {SortColumn, SortDirection} from "../../components/product-list/product-list.directive";
import {clientListData, userListData} from "../../components/product-list/product-list";
import {DecimalPipe} from "@angular/common";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {environment} from "../../../environments/environment";
import {GeneralService} from "./general.service";

const END_POINT = `${environment.SERVICES_URL}`;
const API_KEY = `${environment.API_KEY}`;

interface SearchResult {
  data: any[];
  total: number;
}

interface State {
  page: number;
  pageSize: number;
  searchTerm: string;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
}


@Injectable({
  providedIn: 'root',
})
export class TableService {
  private _loading$ = new BehaviorSubject<boolean>(true);
  private _search$ = new Subject<void>();
  private _getList$ = new BehaviorSubject<any[]>([]);
  private _total$ = new BehaviorSubject<number>(0);
  configuration: any;
  private _data: State = {
    page: 1,
    pageSize: 10,
    searchTerm: '',
    sortColumn: '',
    sortDirection: '',
  };

  compare = (v1: string | number, v2: string | number) => (v1 < v2 ? -1 : v1 > v2 ? 1 : 0);

  constructor(private http: HttpClient, private generalService: GeneralService) {
    this._search$
        .pipe(
            tap(() => this._loading$.next(true)), // Indica que está cargando
            debounceTime(500), // Ajusta el tiempo de espera aquí (500 ms en este caso)
            switchMap(() => this._search()), // Llama al método de búsqueda
            delay(200), // Opcional: Simula un retraso en la respuesta del servidor
            tap(() => this._loading$.next(false)), // Indica que dejó de cargar
        )
        .subscribe((result: any) => {
          this._getList$.next(result.data); // Actualiza la lista
          this._total$.next(result.total); // Actualiza el total de registros
        });

    this._search$.next();
  }

  generateData(configuration: any) {
    this.configuration = configuration;
    this._search$.next();
  }

  get getList$() {
    return this._getList$.asObservable();
  }
  get total$() {
    return this._total$.asObservable();
  }
  get loading$() {
    return this._loading$.asObservable();
  }
  get page() {
    return this._data.page;
  }
  get pageSize() {
    return this._data.pageSize;
  }
  get searchTerm() {
    return this._data.searchTerm;
  }

  set page(page: number) {
    this._set({ page });
  }
  set pageSize(pageSize: number) {
    this._set({ pageSize });
  }
  set searchTerm(searchTerm: string) {
    this._set({ searchTerm });
  }

  set sortColumn(sortColumn: SortColumn) {
    this._set({ sortColumn });
  }
  set sortDirection(sortDirection: SortDirection) {
    this._set({ sortDirection });
  }

  private _set(patch: Partial<State>) {
    Object.assign(this._data, patch);
    this._search$.next();
  }

  private _search(): Observable<SearchResult> {
    if(!this.configuration){
      return of({ data: [], total: 0 });
    }
    const { sortColumn, sortDirection, pageSize, page, searchTerm } = this._data;

    const configTable: any = {
      tb: this.configuration.config,
      filters: this.configuration.filters,
      filterPrincipal: this.configuration.filterPrincipal ?? [],
      page,
      pageSize,
      orderColumn: (sortColumn == "") ? this.configuration.sortColumn : sortColumn,
      orderDirection: (sortDirection == "") ? this.configuration.sortDirection : sortDirection,
      searchTerm
    };

    return this.getData(configTable).pipe(
        map((res: any) => {
          if (res.error) {
            this.generalService.showMessage('warn', `Error ${res.idError}! ${res.message}`);
            return { data: [], total: 0 };
          } else {
            return {
              data: res.message.data,
              total: res.message.pagination.totalRecords // Usar `totalRecords` en lugar de `totalPages`
            };
          }
        }),
        catchError((err: any) => {
          this.generalService.showMessage('warn', `Error! ${JSON.stringify(err)}`);
          return of({ data: [], total: 0 });
        })
    );
  }

  // const { sortColumn, sortDirection, pageSize, page, searchTerm } = this._data;
  //
  // // 1. sort
  // let data = this.sort(userListData, sortColumn, sortDirection);
  //
  // // 2. filter
  // //data = data.filter((list: any) => this.matches(list, searchTerm, this.pipe));
  // const total = data.length;
  //
  // // 3. paginate
  // data = data.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
  // return of({ data, total });

  sort(products: any[] | any, column: SortColumn, direction: string): any[] {
    if (direction === '' || column === '') {
      return products;
    } else {
      return [...products].sort((a, b) => {
        const res = this.compare(a[column], b[column]);
        return direction === 'asc' ? res : -res;
      });
    }
  }

  /**
   * consulta para obtener los registros
   */
  getData(data:any): Observable<any>{
    let params: any = data;
    params.filters = params.filters.slice(0, 2).map((filter: any) => ({
      column: (filter.column.condition) ? filter.column.condition : filter.column.id,
      operator: filter.operator.id,
      isHaving: filter.column.isHaving ?? false,
      value: filter.value,
    }));
    const body = JSON.stringify(params);
    const headers = new HttpHeaders().set('X-Api-Key', `${API_KEY}`);
    this.controlLoader(true);
    return this.http.post<any>(
        `${END_POINT}/table/getdata`,
        body,
        { headers }
    ).pipe(
        tap(() => {
          this.controlLoader(false);
        })
    );
  }

  /**
   * Eliminar row
   */
  deleteRow(data:any): Observable<any>{
    let params: any = {
      action:data.action,
      rowData: data.row
    }
    const body = JSON.stringify(params);
    const headers = new HttpHeaders().set('X-Api-Key', `${API_KEY}`);
    this.controlLoader(true);
    return this.http.post<any>(
        `${END_POINT}/table/delete`,
        body,
        { headers }
    ).pipe(
        tap(() => {
          this.controlLoader(false);
        })
    );
  }

  /**
   * Add/Edit row
   */
  addRow(data:any): Observable<any>{
    const body = JSON.stringify(data);
    const headers = new HttpHeaders().set('X-Api-Key', `${API_KEY}`);
    this.controlLoader(true);
    return this.http.post<any>(
        `${END_POINT}/table/create`,
        body,
        { headers }
    ).pipe(
        tap(() => {
          this.controlLoader(false);
        })
    );
  }

  /**
   * Función para cargar datos de combo
   */
  loadSelectOptions(data:any): Observable<any>{
    const body = JSON.stringify(data);
    const headers = new HttpHeaders().set('X-Api-Key', `${API_KEY}`);
    this.controlLoader(true);
    return this.http.post<any>(
        `${END_POINT}/table/loadselect`,
        body,
        { headers }
    ).pipe(
        tap(() => {
          this.controlLoader(false);
        })
    );
  }

  /**
   * Función para mostrar o remover loading
   */
  controlLoader(show:boolean):void{
    // let elemet: any = document.getElementsByClassName("loader-screen")[0];
    // elemet.style.opacity = 0.8;
    // elemet.style.display = elemet.style.display === 'none' ? '' : 'none';
  }


}
